import { useEffect, useCallback, useMemo, useState, useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useChurch } from "@/hooks/useChurch";
import { useFreemium } from "@/hooks/useFreemium";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Image as ImageIcon, Upload, CalendarDays, FileText } from "lucide-react";
import { TimePicker } from "@/components/ui/time-picker";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format, parseISO } from "date-fns";
import { cn } from "@/lib/utils";
import { sortGeneralFirstByName } from "@/lib/utils";

const MINISTRY_PATH_PREFIX = "Ministry: ";
const MINISTRY_IDS_PATH_PREFIX = "Ministry IDs: ";
const LEGACY_ORG_PATH_PREFIX = "Organization: ";

type Organization = {
  id: string;
  name: string;
  type: string;
  level: string;
  parent_id: string | null;
};

interface EditEvent {
  id: string;
  name: string;
  event_type: string;
  event_date: string;
  start_time: string | null;
  end_time: string | null;
  notes: string | null;
  description: string | null;
  location: string | null;
  flyer_url: string | null;
}

interface Props { open: boolean; onClose: () => void; event?: EditEvent | null; initialDate?: string; initialTime?: string; }

type BulletinTemplate = {
  id: string;
  name: string;
  service_date: string | null;
  event_id: string | null;
};

export default function EventFormDialog({ open, onClose, event, initialDate, initialTime }: Props) {
  const { user } = useAuth();
  const { profile } = useChurch();
  const { isPreview, requireSubscription } = useFreemium();
  const queryClient = useQueryClient();
  const [uploading, setUploading] = useState(false);
  const [selectedOrgId, setSelectedOrgId] = useState("");
  const [locationOpen, setLocationOpen] = useState(false);
  const [linkedBulletinId, setLinkedBulletinId] = useState("");

  // Date picker
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [calendarOpen, setCalendarOpen] = useState(false);

  const handleDateSelect = (date: Date | undefined) => {
    if (date) {
      setSelectedDate(date);
      const yyyy = date.getFullYear();
      const mm = String(date.getMonth() + 1).padStart(2, "0");
      const dd = String(date.getDate()).padStart(2, "0");
      update("event_date", `${yyyy}-${mm}-${dd}`);
      setCalendarOpen(false);
    }
  };



  const [form, setForm] = useState({
    name: "",
    event_date: new Date().toISOString().split("T")[0],
    start_time: "09:00", end_time: "", notes: "",
    description: "", location: "", flyer_url: "",
  });

  const isEditing = !!event;

  const { data: bulletins = [] } = useQuery<BulletinTemplate[]>({
    queryKey: ["bulletin_templates", profile?.id],
    queryFn: async () => {
      if (!profile?.id) return [];
      const { data, error } = await supabase
        .from("bulletin_templates")
        .select("id, name, service_date, service_type, event_id")
        .eq("church_id", profile.id)
        .eq("is_template", false)
        .order("service_date", { ascending: false });
      if (error) throw error;
      return data || [];
    },
    enabled: !!profile?.id,
  });

  useEffect(() => {
    if (event && open) {
      const d = event.event_date ? new Date(event.event_date + "T00:00:00") : new Date();
      setSelectedDate(d);

      // Extract ministry ID from notes
      const noteLines = (event.notes || "").split("\n");
      let ministryId = "";
      const cleanNotes: string[] = [];
      for (const line of noteLines) {
        const trimmed = line.trim();
        if (trimmed.startsWith(MINISTRY_IDS_PATH_PREFIX)) {
          ministryId = trimmed.slice(MINISTRY_IDS_PATH_PREFIX.length).trim();
        } else if (trimmed.startsWith(MINISTRY_PATH_PREFIX) || trimmed.startsWith(LEGACY_ORG_PATH_PREFIX)) {
          // skip ministry/org name lines
        } else {
          cleanNotes.push(line);
        }
      }
      setSelectedOrgId(ministryId);

      // Pre-select bulletin linked to this event
      const linked = bulletins.find((b) => b.event_id === event.id);
      setLinkedBulletinId(linked?.id || "");

      setForm({
        name: event.name || "",
        event_date: event.event_date || new Date().toISOString().split("T")[0],
        start_time: event.start_time || "",
        end_time: event.end_time || "",
        notes: cleanNotes.join("\n").trim(),
        description: event.description || "",
        location: event.location || "",
        flyer_url: event.flyer_url || "",
      });
    } else if (!event && open) {
      // New event — pre-fill date/time if provided
      const dateStr = initialDate || new Date().toISOString().split("T")[0];
      const d = new Date(dateStr + "T00:00:00");
      setSelectedDate(d);
      setSelectedOrgId("");
      setLinkedBulletinId("");
      setForm(prev => ({  
        ...prev,
        name: "",
        event_date: dateStr,
        start_time: initialTime || "09:00",
        end_time: "", notes: "",
        description: "", location: "", flyer_url: "",
      }));
    } else if (!open) {
      // Reset form when dialog closes
      setSelectedDate(new Date());
      setSelectedOrgId("");
      setLinkedBulletinId("");
      setForm({
        name: "",
        event_date: new Date().toISOString().split("T")[0],
        start_time: "09:00", end_time: "", notes: "",
        description: "", location: "", flyer_url: "",
      });
    }
  }, [event, open, initialDate, initialTime, bulletins]);

  const { data: organizations = [] } = useQuery({
    queryKey: ["organizations"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("organizations")
        .select("id, name, type, level, parent_id")
        .order("name");
      if (error) throw error;
      return sortGeneralFirstByName((data || []) as Organization[]);
    },
  });

  const selectableMinistries = useMemo(
    () => sortGeneralFirstByName(organizations.filter((org) => org.type === "ministry" || !org.type)),
    [organizations]
  );

  // Address autocomplete via Nominatim (OpenStreetMap)
  const [addressSuggestions, setAddressSuggestions] = useState<string[]>([]);
  const addressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const searchAddress = useCallback((query: string) => {
    if (addressTimerRef.current) clearTimeout(addressTimerRef.current);
    if (query.length < 3) { setAddressSuggestions([]); return; }
    addressTimerRef.current = setTimeout(async () => {
      try {
        const res = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&addressdetails=1&limit=5&q=${encodeURIComponent(query)}`,
          { headers: { "Accept-Language": "en" } }
        );
        const data = await res.json();
        const suggestions = (data as { display_name: string }[]).map((r) => r.display_name);
        setAddressSuggestions(suggestions);
      } catch {
        setAddressSuggestions([]);
      }
    }, 350);
  }, []);

  const uploadFlyer = async (file: File) => {
    if (!profile?.id) return;
    setUploading(true);
    try {
      const ext = file.name.split(".").pop();
      const path = `church/${profile.id}/flyers/${crypto.randomUUID()}.${ext}`;
      const { error } = await supabase.storage.from("church-assets").upload(path, file);
      if (error) throw error;
      const { data } = supabase.storage.from("church-assets").getPublicUrl(path);
      setForm(f => ({ ...f, flyer_url: data.publicUrl }));

    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Upload failed";
      toast.error(message);
    } finally {
      setUploading(false);
    }
  };

  const mutation = useMutation({
    mutationFn: async () => {
      const selectedOrg = selectableMinistries.find((org) => org.id === selectedOrgId);
      const selectedOrgName = selectedOrg?.name || "";
      const selectedOrgIdsPath = selectedOrg?.id || "";
      const derivedEventType = selectedOrgName || "General Event";

      const existingNotes = form.notes.trim();
      const cleanedNotes = existingNotes
        .split("\n")
        .filter((line) => {
          const trimmed = line.trim();
          return !trimmed.startsWith(MINISTRY_PATH_PREFIX) && !trimmed.startsWith(LEGACY_ORG_PATH_PREFIX);
        })
        .join("\n")
        .trim();

      const notesWithOrg = [
        selectedOrgName ? `${MINISTRY_PATH_PREFIX}${selectedOrgName}` : "",
        selectedOrgIdsPath ? `${MINISTRY_IDS_PATH_PREFIX}${selectedOrgIdsPath}` : "",
        cleanedNotes,
      ].filter(Boolean).join("\n") || null;

      const payload = {
        name: form.name.trim(),
        event_type: derivedEventType,
        category: "service",
        event_date: form.event_date,
        start_time: form.start_time || null,
        end_time: form.end_time || null,
        notes: notesWithOrg,
        description: form.description.trim() || null,
        location: form.location.trim() || null,
        flyer_url: form.flyer_url || null,
      };

      let eventId: string;
      if (isEditing && event) {
        const { error } = await supabase.from("events").update(payload).eq("id", event.id);
        if (error) throw error;
        eventId = event.id;
      } else {
        const { data: inserted, error } = await supabase
          .from("events")
          .insert({ ...payload, created_by: user?.id || null, church_id: profile?.id })
          .select("id")
          .single();
        if (error) throw error;
        eventId = inserted.id;
      }

      // Unlink any bulletin previously attached to this event if a different one is now chosen
      const previouslyLinked = bulletins.find(
        (b) => b.event_id === eventId && b.id !== linkedBulletinId
      );
      if (previouslyLinked) {
        await supabase
          .from("bulletin_templates")
          .update({ event_id: null })
          .eq("id", previouslyLinked.id)
          .eq("is_template", false);
      }
      if (linkedBulletinId) {
        await supabase
          .from("bulletin_templates")
          .update({ event_id: eventId })
          .eq("id", linkedBulletinId)
          .eq("is_template", false);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["events"] });
      queryClient.invalidateQueries({ queryKey: ["bulletin_templates"] });
      
      onClose();
      setSelectedOrgId("");
      setLinkedBulletinId("");
      setSelectedDate(new Date());
      setForm({
        name: "",
        event_date: new Date().toISOString().split("T")[0],
        start_time: "09:00", end_time: "", notes: "",
        description: "", location: "", flyer_url: "",
      });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Event name is required"); return; }
    if (!requireSubscription()) return;
    mutation.mutate();
  };
  const update = (key: string, value: string) => setForm(p => ({ ...p, [key]: value }));

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader><SheetTitle className="font-heading">{isEditing ? "Edit Event" : "Create Event"}</SheetTitle></SheetHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Event Name *</Label>
            <Input value={form.name} onChange={(e) => update("name", e.target.value)} placeholder="e.g. Sunday Service - March 10" required />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Ministry</Label>
              <Select value={selectedOrgId || "__none"} onValueChange={(v) => setSelectedOrgId(v === "__none" ? "" : v)}>
                <SelectTrigger><SelectValue placeholder="Select ministry" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">None</SelectItem>
                  {selectableMinistries.map((org) => <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Date *</Label>
              <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" className={cn("w-full justify-start text-left font-normal", !form.event_date && "text-muted-foreground")}>
                    <CalendarDays className="mr-2 h-4 w-4" />
                    {form.event_date ? format(parseISO(form.event_date), "MMM d, yyyy") : "Pick a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar mode="single" selected={selectedDate} onSelect={handleDateSelect} initialFocus />
                </PopoverContent>
              </Popover>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
            <Label>Start Time</Label>
            <TimePicker value={form.start_time || "09:00"} onChange={(v) => update("start_time", v)} />
          </div>
          <div className="space-y-2">
            <Label>End Time</Label>
            <TimePicker value={form.end_time || ""} onChange={(v) => update("end_time", v)} placeholder="Optional" />
          </div>
          </div>

          <div className="space-y-2 relative">
            <Label>Location</Label>
            <Input
              value={form.location}
              onChange={(e) => { update("location", e.target.value); searchAddress(e.target.value); setLocationOpen(true); }}
              onFocus={() => { if (addressSuggestions.length > 0) setLocationOpen(true); }}
              onBlur={() => setTimeout(() => setLocationOpen(false), 200)}
              placeholder="Start typing an address..."
              autoComplete="off"
            />
            {locationOpen && addressSuggestions.length > 0 && (
              <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-popover border border-border rounded-md shadow-md max-h-48 overflow-y-auto">
                {addressSuggestions.map((addr, i) => (
                  <button
                    key={i}
                    type="button"
                    className="w-full text-left px-3 py-2 text-sm hover:bg-accent hover:text-accent-foreground transition-colors border-b border-border/30 last:border-0"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => { update("location", addr); setAddressSuggestions([]); setLocationOpen(false); }}
                  >
                    {addr}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea value={form.description} onChange={(e) => update("description", e.target.value)} rows={2} placeholder="Brief description of the event..." />
          </div>

          {/* Flyer Upload */}
          <div className="space-y-2">
            <Label>Event Flyer</Label>
            {form.flyer_url ? (
              <div className="relative rounded-xl overflow-hidden border border-border">
                <img src={form.flyer_url} alt="Flyer" className="w-full h-32 object-cover" />
                <Button type="button" variant="destructive" size="sm" className="absolute top-2 right-2 h-7 text-xs" onClick={() => update("flyer_url", "")}>
                  Remove
                </Button>
              </div>
            ) : (
              <label className="flex items-center justify-center gap-2 border-2 border-dashed border-border rounded-xl p-6 cursor-pointer hover:border-primary/40 hover:bg-secondary/30 transition-colors">
                <input type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadFlyer(f); }} />
                {uploading ? (
                  <span className="text-sm text-muted-foreground">Uploading...</span>
                ) : (
                  <>
                    <Upload className="w-5 h-5 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">Click to upload flyer image</span>
                  </>
                )}
              </label>
            )}
          </div>

          <div className="space-y-2">
            <Label>Notes</Label>
            <Textarea value={form.notes} onChange={(e) => update("notes", e.target.value)} rows={2} placeholder="Optional notes..." />
          </div>

          {/* Linked Bulletin */}
          <div className="space-y-2">
            <Label className="flex items-center gap-1.5"><FileText className="w-3.5 h-3.5" />Linked Bulletin</Label>
            <Select value={linkedBulletinId || "__none"} onValueChange={(v) => setLinkedBulletinId(v === "__none" ? "" : v)}>
              <SelectTrigger>
                <SelectValue placeholder="Attach a bulletin..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none">None</SelectItem>
                {bulletins.map((b) => (
                  <SelectItem key={b.id} value={b.id}>
                    {b.name}{b.service_date ? ` — ${format(parseISO(b.service_date), "MMM d, yyyy")}` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={mutation.isPending || uploading}>
              {isPreview && <span className="mr-2"></span>}
              {mutation.isPending ? (isEditing ? "Saving..." : "Creating...") : (isEditing ? "Save Changes" : "Create Event")}
            </Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
}
